import { z } from 'zod';
import {
  ErrorCode,
  PARTNER_MATERIAL_LOCALES,
  type PartnerMaterialLocale,
  type PartnerMaterialText
} from '@bliks-io/contracts';

/** Every language as an always-present string, which is what form inputs bind to. */
export type LocalizedFormText = Record<PartnerMaterialLocale, string>;

export const LocalizedFormTextSchema = z.object({
  de: z.string().trim().min(1, ErrorCode.FIELD_REQUIRED),
  en: z.string(),
  fr: z.string(),
  hu: z.string()
});

export const OptionalLocalizedFormTextSchema = LocalizedFormTextSchema.extend({ de: z.string() });

export function toLocalizedFormText(
  text: Partial<Record<PartnerMaterialLocale, string>> | undefined
): LocalizedFormText {
  return { de: text?.de ?? '', en: text?.en ?? '', fr: text?.fr ?? '', hu: text?.hu ?? '' };
}

/** Only the filled-in languages; empty ones fall back to German on the partner page. */
export function compactText(
  text: LocalizedFormText
): Partial<Record<PartnerMaterialLocale, string>> {
  const filled: Partial<Record<PartnerMaterialLocale, string>> = {};
  for (const locale of PARTNER_MATERIAL_LOCALES) {
    const value = text[locale].trim();
    if (value) filled[locale] = value;
  }
  return filled;
}

/** A required text (German present after validation) in contract shape. */
export function requiredText(text: LocalizedFormText): PartnerMaterialText {
  return { ...compactText(text), de: text.de.trim() };
}

/** An optional text, or null when every language was emptied. */
export function optionalText(
  text: LocalizedFormText
): Partial<Record<PartnerMaterialLocale, string>> | null {
  const filled = compactText(text);
  return Object.keys(filled).length ? filled : null;
}
